import type BackOfficeConnector from "data/backOfficeConnector";
import type EventBus from "services/eventBus";
import RepositoryBase from "./repositoryBase";
import type { IPagedResults, IPagination } from "data/types";
import type SearchAccountBalanceCheckRequestsRequest from "entities/searchAccountBalanceCheckRequestsRequest";
import { serializeFilters } from "utils/filters";
import AccountBalanceCheckRequestsResponse from "entities/accountBalanceCheckRequestsResponse";
import AccountBalanceCheckRequestsTotalCountResponse from "entities/accountBalanceCheckRequestsTotalCountResponse";
import AccountBalanceCheckRequestDetailResponse from "entities/accountBalanceCheckRequestDetailResponse";
import CreateAccountBalanceCheckRequestRequest from "entities/createAccountBalanceCheckRequestRequest";
import type { AxiosResponse } from "axios";

export default class AccountingBalanceRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  search(
    filters: SearchAccountBalanceCheckRequestsRequest,
    pagination: IPagination
  ): Promise<AccountBalanceCheckRequestsResponse> {
    return this.callApi(
      api =>
        api
          .all("accounting")
          .all("account_balance_check_request_search")
          .postEntity({ ...serializeFilters(filters, false), ...pagination }, AccountBalanceCheckRequestsResponse),
      { ownApiFactory: () => this.apiFactory({ customVersion: "2" }) }
    );
  }

  searchTotalCount(
    filters: SearchAccountBalanceCheckRequestsRequest,
    pagination: IPagination,
    abortSignal: AbortSignal
  ): Promise<IPagedResults<AccountBalanceCheckRequestsTotalCountResponse>> {
    return this.callApi(
      api =>
        api
          .all("accounting")
          .all("account_balance_check_request_search")
          .postPagedEntities(
            { ...serializeFilters(filters, false), ...pagination },
            AccountBalanceCheckRequestsTotalCountResponse,
            abortSignal
          ),
      { ownApiFactory: () => this.apiFactory({ customVersion: "2" }) }
    );
  }

  get(id: string) {
    return this.callApi(
      api => api.all("accounting").one("account_balance_check_requests", id).getEntity(AccountBalanceCheckRequestDetailResponse),
      { ownApiFactory: () => this.apiFactory({ customVersion: "2" }) }
    );
  }

  getResultFile(id: string): Promise<AxiosResponse<File>> {
    return this.callApi(
      api => api.all("accounting").one("account_balance_check_requests", id).all("result_file").getRaw() as any,
      {
        ownApiFactory: () => this.apiFactory({ customVersion: "2" }),
      }
    );
  }

  create(entity: CreateAccountBalanceCheckRequestRequest) {
    return this.callApi(
      api =>
        api
          .all("accounting")
          .all("account_balance_check_requests")
          .postEntity<CreateAccountBalanceCheckRequestRequest>(entity, CreateAccountBalanceCheckRequestRequest),
      { ownApiFactory: () => this.apiFactory({ customVersion: "2" }) }
    );
  }
}
